import React, { forwardRef } from 'react'

import { TFunction } from 'i18next'
import { templateFormatter } from 'input-format'
import { useFormContext, Validate } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { NumberFormatValues } from 'react-number-format'

import { ArcadeIcon, ClearFieldIcon, LockLockedFillIcon, LockLockedIcon } from '@arcade/web/icons/react'
import {
  ArcadeFieldLabel,
  ArcadeInput,
  ArcadeInputProps,
  ArcadePasswordInput,
  spacing,
  Stack,
} from '@cash-design-system/react'
import {
  FormBlockerElementTextInputElement,
  FormBlockerElementTextInputElementInputField,
  FormBlockerElementTextInputElementInputFieldSecureFieldAccessory,
  FormBlockerElementTextInputElementInputFieldTrailingFieldAccessory,
  FormBlockerElementTextInputElementKeyboardType,
  FormBlockerElementTextInputElementSecurity,
  FormBlockerElementTextInputElementValidation,
} from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import logger from '@cash-web/shared/util-logger'
import { ConfirmationCodeInput, PhoneInput, SsnInput, useCombinedInputRef } from '@cash-web/shared-feature-form'
import { getNestedValue } from '@cash-web/shared-feature-form'
import { MaskedInput } from '@cash-web/shared-feature-form'
import { toPartialMatch } from '@cash-web/shared-util-validation'

const getNumberFormatFromField = (validations?: FormBlockerElementTextInputElementValidation[]) => {
  const validationWithFormat = validations?.find(validation => validation.template?.template)

  if (
    validationWithFormat &&
    validationWithFormat.template?.template &&
    validationWithFormat.template?.template_placeholder_character
  ) {
    const { template, template_placeholder_character, user_insertable_format_characters } =
      validationWithFormat.template
    const mask = template
      .split('')
      .map(char => {
        if (char === template_placeholder_character || user_insertable_format_characters?.includes(char)) {
          return 'x'
        } else {
          return char
        }
      })
      .join('')

    return (value: string) => {
      const formattedText = templateFormatter(mask)(value).text

      if (validations?.some(validation => validation.template?.user_insertable_format_characters)) {
        const templateSplit = template?.split('')
        const splitFormattedText = formattedText.split('')

        // Check if the formatted text has the user insertable character
        const index = templateSplit.findIndex(char => user_insertable_format_characters?.includes(char))
        const hasUserInsertableChar = index && splitFormattedText[index] === user_insertable_format_characters

        // If no user insertable character is found, insert character to match template
        if (!hasUserInsertableChar && splitFormattedText.length >= index) {
          let shiftIndex = 0
          const updatedValueWithTemplate = templateSplit.reduce((acc, char, index) => {
            const currIndex = index - shiftIndex

            if (user_insertable_format_characters?.includes(char)) {
              acc += char
              if (!user_insertable_format_characters?.includes(value[currIndex])) {
                shiftIndex++
              }
            } else if (char === template_placeholder_character && value[currIndex]) {
              acc += value[currIndex]
            } else {
              acc += char
              shiftIndex++
            }

            return acc
          }, '')

          return updatedValueWithTemplate
        }
      }

      return formattedText
    }
  }

  return undefined
}

const ShowHideInput = forwardRef<HTMLInputElement, React.ComponentProps<typeof ArcadeInput> & { inputMode?: string }>(
  (props, ref) => {
    const { t } = useTranslation()
    const showLabel = t('Show text', { context: 'button associated with secure text input to reveal masked text' })
    const hideLabel = t('Hide text', { context: 'button associated with secure text input to hide visible text' })
    return (
      <ArcadePasswordInput
        {...props}
        fieldLabelProps={{
          label: '',
        }}
        showPasswordLabel={showLabel}
        hidePasswordLabel={hideLabel}
        isNewPassword={false}
        ref={ref}
      />
    )
  }
)

const InputWithClearButton = forwardRef<HTMLInputElement, React.ComponentProps<typeof ArcadeInput>>((props, ref) => {
  const { t } = useTranslation()
  const { setValue } = useFormContext()
  const { combinedRef, inputRef } = useCombinedInputRef(ref)

  const handleClear = () => {
    const fieldName = props.name

    // This should never happen, but adding it here so the types behave below.
    if (!fieldName) {
      logger.error(new Error('InputWithClearButton: fieldName prop is required'))
      return
    }

    // Clear the value
    setValue(fieldName, '', {
      shouldValidate: false,
      shouldDirty: true,
    })

    // Focus input so user can immediately start typing.
    inputRef.current?.focus()
  }

  return (
    <ArcadeInput
      {...props}
      type={'text'}
      TrailingIcon={ClearFieldIcon as ArcadeIcon}
      trailingIconClickProps={{
        onClick: handleClear,
        ariaLabel: t('Clear input', { context: 'icon label on input' }),
      }}
      ref={combinedRef}
    />
  )
})

const LockInput = forwardRef<HTMLInputElement, React.ComponentProps<typeof ArcadeInput>>((props, ref) => {
  return <ArcadeInput {...props} TrailingIcon={LockLockedFillIcon as ArcadeIcon} ref={ref} />
})

const getInputComponent = (field: FormBlockerElementTextInputElementInputField) => {
  if (field.trailing_field_accessory === 'CLEAR_BUTTON') {
    return InputWithClearButton
  }

  if (field.security !== FormBlockerElementTextInputElementSecurity.SECURE) {
    return ArcadeInput
  }

  if (field.trailing_field_accessory) {
    switch (field.trailing_field_accessory) {
      case FormBlockerElementTextInputElementInputFieldTrailingFieldAccessory.SECURE_FIELD_SHOW_HIDE:
        return ShowHideInput
      case FormBlockerElementTextInputElementInputFieldTrailingFieldAccessory.SECURE_FIELD_LOCK:
        return LockInput
      case FormBlockerElementTextInputElementInputFieldTrailingFieldAccessory.ABSENT:
        return ArcadeInput
      default:
        // What to do if logged:
        // - Add support for the missing trailing_field_accessory.
        logger.warn(
          new Error(`TextInputElement: unsupported trailing_field_accessory member - ${field.trailing_field_accessory}`)
        )
    }
  }

  // This will be deprecated eventually, but not all server libraries have migrated to trailing_field_accessory.
  switch (field.secure_field_accessory) {
    case FormBlockerElementTextInputElementInputFieldSecureFieldAccessory.SHOW_HIDE:
      return ShowHideInput
    case FormBlockerElementTextInputElementInputFieldSecureFieldAccessory.LOCK:
      return LockInput
    case FormBlockerElementTextInputElementInputFieldSecureFieldAccessory.NONE:
      return ArcadeInput
    default:
      // What to do if logged:
      // - Add support for the missing secure_field_accessory.
      logger.warn(
        new Error(`TextInputElement: unsupported secure_field_accessory member - ${field.secure_field_accessory}`)
      )
      return ArcadeInput
  }
}

type FieldInputProps = {
  name: string
  type: string
  value?: string
  autoFocus?: boolean
  field: FormBlockerElementTextInputElementInputField
  placeholder: string
  errorText?: string
  validate: Validate<string>
  defaultValue?: string
  disabled?: boolean
  helpText?: ArcadeInputProps['helpText']
  HelpTextIcon?: ArcadeInputProps['HelpTextIcon']
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
}

const CustomInputHintText = {
  ConfirmationCode: 'Confirmation code',
  fullSSN: '9-digit SSN',
  Last4OfSSN: 'Last 4 of SSN',
  PhoneNumber: 'Mobile number',
} as const

const FieldInput = (fieldInputProps: FieldInputProps) => {
  const { register } = useFormContext()
  const hintText = fieldInputProps.field.hint_text

  if (hintText === CustomInputHintText.ConfirmationCode) {
    return <ConfirmationCodeInput {...fieldInputProps} />
  }

  if (hintText === CustomInputHintText.fullSSN) {
    return <SsnInput {...fieldInputProps} variant="FULL" />
  }

  if (hintText === CustomInputHintText.Last4OfSSN) {
    return <SsnInput {...fieldInputProps} variant="LAST_FOUR" />
  }

  if (hintText === CustomInputHintText.PhoneNumber) {
    return <PhoneInput {...fieldInputProps} />
  }

  const { field, name, validate, defaultValue, ...props } = fieldInputProps

  // add the start and end of string anchors to the regex otherwise partial match will always match.
  const validations = field.validations
    ?.filter(v => v.regex != null)
    ?.map(v => {
      try {
        return toPartialMatch(
          new RegExp(`${v.regex?.startsWith('^') ? '' : '^'}${v.regex}${v.regex?.endsWith('$') ? '' : '$'}`)
        )
      } catch {
        // in the event it fails to parse the regex, return null
        logger.error(new Error('Failed to create a partial match for regex:' + v.regex))
        return null
      }
    })

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (validations) {
      const input = event.target as HTMLInputElement

      // Skip validations if the user is holding a special key. They might be pasting, selecting
      // text, or otherwise performing actions we cannot validate against with onKeyPress
      if (event.altKey || event.ctrlKey || event.metaKey) {
        return
      }

      let value = input.value
      // onKeyPress is not fired for special keys (Backspace, Shift, Alt), and is inconsistently
      // fired if modifiers are pressed with normal keys, but is fired for Enter.
      // This block prevents validating on [input value] + [Enter] when trying to submit the form.
      if (event.key !== 'Enter') {
        value = value + event.key
      }

      validations.forEach(regex => {
        if (regex == null) {
          return
        }
        // partial match will only return false if we do not partial match
        if (!regex.test(value)) {
          event.stopPropagation()
          event.preventDefault()
        }
      })
    }
  }

  const CustomInput = getInputComponent(field)

  if (field.keyboardType === FormBlockerElementTextInputElementKeyboardType.NUMBER_PAD) {
    const format = getNumberFormatFromField(field.validations)

    if (format) {
      return (
        <MaskedInput
          {...props}
          CustomInput={CustomInput}
          format={format}
          initialFormattedValue={defaultValue}
          isAllowed={(values: NumberFormatValues) => {
            return Boolean(validations?.filter(regex => regex !== null).every(regex => regex.test(values.value)))
          }}
          name={name}
          onBlur={props.onBlur as (event: React.FocusEvent) => void}
          readOnly={field.disabled}
          rules={{ validate }}
        />
      )
    }
  }

  const { ref, ...fieldProps } = register(name, { validate })

  return (
    <CustomInput
      {...fieldProps}
      {...props}
      isInvalid={!!props.errorText}
      name={name}
      readOnly={field.disabled}
      defaultValue={defaultValue}
      onKeyPress={handleKeyPress}
      ref={ref}
    />
  )
}

interface Props extends FormBlockerElementTextInputElement {
  formSectionIndex: number
  elementId?: string
  shouldAutoFocus?: boolean
}
const placeholderReplacement: Record<string, string> = {
  'Authentication code': 'Code',
  'Confirmation code': 'Code',
}

export function getInputFieldKey(formSectionIndex: number, inputFieldIndex: number) {
  return `${formSectionIndex}.text_input_result.input_field_values.${inputFieldIndex}`
}

const customErrorMessages = (elementId: string | undefined, t: TFunction, hint_text?: string) => {
  switch (elementId) {
    case 'EMPLOYER_NAME_INPUT_ID':
      return t('Enter the name of your employer.', { context: 'modal error message under input field' })
    case 'CAPTURE_PHONE_INPUT':
      return '' // to match mobile
    default:
      return t('Invalid {field}', {
        context: 'modal error message under input field',
        field: (hint_text || 'field').toLowerCase(),
      })
  }
}

export function ArcadeTextInputElement({
  input_fields,
  elementId,
  formSectionIndex,
  security_text,
  shouldAutoFocus,
}: Props): React.ReactElement | null {
  const {
    register,
    formState: { errors },
  } = useFormContext()
  const { t } = useTranslation()
  if (!input_fields) {
    return null
  }
  return (
    <div>
      <input type="hidden" value={elementId} {...register(`${formSectionIndex}.id`)} />

      <Stack space={spacing.m}>
        {input_fields.map((field, index) => {
          const isLastField = index === input_fields.length - 1
          const key = getInputFieldKey(formSectionIndex, index)
          const error = getNestedValue(
            [formSectionIndex, 'text_input_result', 'input_field_values', index],
            errors
          ) as { type?: string; message?: string; ref?: HTMLInputElement }
          const errorText = error?.message

          const { hint_text, keyboardType, label_text, prefill_text, security, validations } = field

          let type = 'text'
          if (security === FormBlockerElementTextInputElementSecurity.SECURE) {
            type = 'password'
          } else if (keyboardType === FormBlockerElementTextInputElementKeyboardType.EMAIL) {
            type = 'email'
          } else if (keyboardType === FormBlockerElementTextInputElementKeyboardType.NUMBER_PAD) {
            // if we change this to number, we only have an up/down arrow to increment/decrement
            type = 'text'
          }

          const showSecurityText = security_text && isLastField && !error
          const isCustomInput = Object.values(CustomInputHintText).some(customInput => hint_text?.includes(customInput))
          const labelText = label_text ?? hint_text ?? ''
          const shouldIncludeDisabledFieldValueInFormSubmission = Boolean(field.disabled && prefill_text)

          return (
            <div key={key}>
              <ArcadeFieldLabel label={isCustomInput ? '' : labelText}>
                <FieldInput
                  autoFocus={shouldAutoFocus && index === 0}
                  name={key}
                  field={field}
                  disabled={field.disabled}
                  placeholder={hint_text != null ? placeholderReplacement[hint_text] ?? hint_text : ''}
                  type={type}
                  defaultValue={prefill_text}
                  errorText={errorText}
                  validate={value => {
                    const invalid = validations?.find(
                      validation => validation.regex && !RegExp(validation.regex).test(value)
                    )

                    if (invalid) {
                      return customErrorMessages(elementId, t, hint_text)
                    }

                    return true
                  }}
                  {...(showSecurityText
                    ? {
                        helpText: security_text,
                        HelpTextIcon: LockLockedIcon as ArcadeIcon,
                      }
                    : {})}
                />
              </ArcadeFieldLabel>
              {shouldIncludeDisabledFieldValueInFormSubmission && (
                <input type="hidden" defaultValue={prefill_text} {...register(key)} />
              )}
            </div>
          )
        })}
      </Stack>
    </div>
  )
}
